// VersionModal.js
import React from 'react';
import { X, Sparkles, CheckCircle, AlertCircle, Info } from 'lucide-react';

const VersionModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const updates = [
    {
      version: '1.3',
      date: '2025-12-23',
      type: 'major',
      changes: [
        { type: 'feature', text: 'Full UI retheme: new palette, consistent components, and updated typography' },
        { type: 'feature', text: 'Redesigned login page with hero layout and improved accessibility' },
        { type: 'improvement', text: 'Domain guard now shows a styled access-restricted card with SSO options' },
        { type: 'improvement', text: 'Global modal styles refreshed for consistency across dashboards and detail views' },
        { type: 'improvement', text: 'New app icons and favicon generated for PWA support' },
        { type: 'improvement', text: 'Added ARIA attributes and keyboard handlers for better accessibility' }
      ]
    },
    {
      version: '1.2.1',
      date: '2025-12-17',
      type: 'Patch',
      changes: [
        { type: 'fix', text: 'Adjusted default email sorting to show latest emails first' }
      ]
    },
    {
      version: '1.2',
      date: '2025-12-16',
      type: 'minor',
      changes: [
        { type: 'feature', text: 'Order Type displayed as color-coded email-style tags' },
        { type: 'feature', text: 'Clickable Order Type tags for instant filtering' },
        { type: 'improvement', text: 'Improved front-end readability and scanning of email data' }
      ]
    },
    {
      version: '1.1',
      date: '2025-11-22',
      type: 'major',
      changes: [
        { type: 'feature', text: 'Microsoft Authentication integration for enhanced security' },
        { type: 'feature', text: 'Search bar with intelligent auto-suggestion functionality' }
      ]
    },
    {
      version: '1.0',
      date: '2025-10-17',
      type: 'major',
      changes: [
        { type: 'feature', text: 'Initial release of IMS Dashboard' },
        { type: 'feature', text: 'Real-time email classification tracking' },
        { type: 'feature', text: 'Interactive charts and visualizations' },
        { type: 'feature', text: 'Advanced filtering and search capabilities' },
        { type: 'feature', text: 'Sortable data tables with pagination' },
        { type: 'feature', text: 'Priority-based email categorization' },
        { type: 'feature', text: 'Responsive design for mobile and desktop' },
        { type: 'feature', text: 'Export to CSV functionality' },
        { type: 'feature', text: 'Live connection status monitoring' },
        { type: 'feature', text: 'Detailed email view modal' }
      ]
    }
  ];

  const getTypeIcon = (type) => {
    switch (type) {
      case 'feature':
        return <Sparkles size={16} color="var(--success)" />;
      case 'fix':
        return <CheckCircle size={16} color="var(--accent-color)" />;
      case 'warning':
        return <AlertCircle size={16} color="var(--warning, #f59e0b)" />;
      default:
        return <Info size={16} color="var(--neutral)" />;
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content" 
        style={{ maxWidth: '700px', maxHeight: '85vh' }} 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header" style={{
          background: 'var(--modal-header-gradient)'
        }}>
          <div className="modal-header-content">
            <div className="modal-header-left">
              <div className="modal-icon-wrapper">
                <Sparkles size={24} color="var(--accent-color)" />
              </div>
              <div>
                <h3 className="modal-title" style={{ fontSize: '1.75rem' }}>
                  Version Updates
                </h3>
                <p className="modal-subtitle" style={{ fontSize: '1rem', color: 'var(--muted)' }}>
                  What's new in IMS Dashboard
                </p>
              </div>
            </div>
            <button className="modal-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {updates.map((update, idx) => (
              <div key={idx} style={{
                background: 'rgba(30, 41, 59, 0.6)',
                borderRadius: '1rem',
                padding: '1.5rem',
                border: '1px solid rgba(71, 85, 105, 0.3)',
                backdropFilter: 'blur(8px)'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: '1.25rem',
                  paddingBottom: '1rem',
                  borderBottom: '1px solid rgba(71, 85, 105, 0.3)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: 'var(--accent-color)',
                      fontFamily: 'monospace'
                    }}>
                      v{update.version}
                    </span>
                    <span style={{
                      background: update.type === 'major' 
                        ? 'linear-gradient(to right, var(--accent-start), var(--accent-end))' 
                        : 'linear-gradient(to right, var(--success), #059669)',
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '700',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      {update.type}
                    </span>
                  </div>
                  <span style={{
                    color: 'var(--muted)',
                    fontSize: '0.875rem',
                    fontFamily: 'monospace'
                  }}>
                    {update.date}
                  </span>
                </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {update.changes.map((change, changeIdx) => (
                    <div 
                      key={changeIdx}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '0.75rem',
                        padding: '0.75rem',
                            background: 'rgba(51, 65, 85, 0.4)',
                        borderRadius: '0.5rem',
                            border: '1px solid rgba(71, 85, 105, 0.2)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(51, 65, 85, 0.6)';
                        e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(51, 65, 85, 0.4)';
                        e.currentTarget.style.borderColor = 'rgba(71, 85, 105, 0.2)';
                      }}
                    >
                      <div style={{ marginTop: '0.125rem', flexShrink: 0 }}>
                        {getTypeIcon(change.type)}
                      </div>
                      <span style={{
                        color: 'var(--e2e8f0, #e2e8f0)',
                        fontSize: '1rem',
                        lineHeight: '1.5'
                      }}>
                        {change.text}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Footer Info */}
          <div style={{
            marginTop: '2rem',
            padding: '1.25rem',
            background: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '0.75rem',
            border: '1px solid rgba(96, 165, 250, 0.2)',
            textAlign: 'center'
          }}>
            <p style={{
              color: '#93c5fd',
              fontSize: '0.875rem',
              margin: 0,
              lineHeight: '1.6'
            }}>
              <strong>Stay Updated:</strong> Check back regularly for new features and improvements.
              <br />
              For support or feedback, contact the development team.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VersionModal;
